import React, {useEffect} from "react";
import {IMetadataState} from "../../reducers/createNewProjectReducer.ts";
import Switch from "../Switch.tsx";
import {GranularityEnum} from "../../models/enums/granularity.enum.ts";
import {VisualTagsEnum} from "../../models/enums/visualTags.enum.ts";
import {snakeCaseToSentenceCase, titleCase} from "../../utils/utils.ts";
import Tooltip from "../Tooltip.tsx";
import {Box, Slider, SliderFilledTrack, SliderThumb, SliderTrack} from "@chakra-ui/react";
import {
  GLIX_SHOT_DETECTION_SENSITIVITY_DEFAULT,
  LANGUAGE_CONFIGURATION_BOUNDS
} from "../../models/constants/constants.ts";

interface MetadataStepProps {
  metadataState: IMetadataState
  toggleDialogueList: (value: boolean) => void;
  toggleExtendedMetadata: (value: boolean) => void;
  setGranularity: (granularity: GranularityEnum) => void;
  setShotChangeDetectionSensitivity: (value: number) => void
  setVisualTags: (visualTags: VisualTagsEnum[]) => void;
}

const MetadataStep: React.FC<MetadataStepProps> = (
  {
    metadataState,
    toggleDialogueList,
    toggleExtendedMetadata,
    setGranularity,
    setShotChangeDetectionSensitivity,
    setVisualTags,
  }: MetadataStepProps) => {

  useEffect(() => {
    const isSelected: boolean = Boolean(metadataState?.isExtendedMetadata?.isSelected);
    if (!isSelected) {
      const timeout = setTimeout(() => {
        setGranularity(GranularityEnum.GLIX);
        setVisualTags([
          VisualTagsEnum.PLOT_CONTEXT,
          VisualTagsEnum.VIOLENCE,
          VisualTagsEnum.SEXUAL,
          VisualTagsEnum.SUBSTANCE_USE,
        ]);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [
    metadataState?.isExtendedMetadata?.isSelected,
    setGranularity,
    setVisualTags,
  ]);

  const handleVisualTags = (visualTag: VisualTagsEnum) => {
    const currentVisualTags: VisualTagsEnum[] = metadataState?.isExtendedMetadata?.visualTags || [];
    if (currentVisualTags.includes(visualTag)) {
      setVisualTags(currentVisualTags.filter(tag => tag !== visualTag));
    } else {
      if (currentVisualTags.length < 4) {
        setVisualTags([...currentVisualTags, visualTag]);
      }
    }
  }

  return (
    <div className="flex flex-col w-full">
      <div
        className={`w-full p-3 rounded-md border-brandPurple-500 ${metadataState?.isDialogueListSelected ? " border-2" : " border"}`}
      >
        <div className="flex items-center justify-between mb-1 ">
          <p className="text-xl font-semibold text-white">
            Dialogue List
          </p>
          <Switch
            value={metadataState?.isDialogueListSelected ?? false}
            setValue={toggleDialogueList}/>
        </div>
        <p className="text-xs text-n-400">
          Generate a comprehensive shot-by-shot Dialogue List (CCSL) PDF including characters information, visual
          description and utterance level speaker tagging.
        </p>
        <div className={`mt-4 ${!metadataState?.isDialogueListSelected && "opacity-40"}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xl font-semibold text-white">
              Extended Metadata
            </p>
            <Tooltip
              text={"Requires Dialogue List"}
              children={
                <Switch
                  className={!metadataState?.isDialogueListSelected ? "pointer-events-none" : ""}
                  value={Boolean(metadataState?.isDialogueListSelected && metadataState?.isExtendedMetadata?.isSelected)}
                  setValue={(value: boolean) => metadataState?.isDialogueListSelected && toggleExtendedMetadata(value)}
                />
              }
              position={"top"}
              condition={!metadataState?.isDialogueListSelected}
            />
          </div>
          <p className="text-xs text-n-400">
            Access an advanced detailed analysis that provides deeper context about cinematography choices,
            character development, narrative significance and pivotal dialogue markers.
            Perfect for directors, editors, and content analysts who need to quickly identify key storytelling moments,
            track character development, and understand the visual language of each scene.
          </p>
          {/*  collapse using react and tailwind*/}
          <div
            className={`transition-all ${metadataState?.isExtendedMetadata?.isSelected ? "duration-1000" : "duration-500"} ease-in-out overflow-hidden ${
              metadataState?.isExtendedMetadata?.isSelected ? "max-h-[700px]" : "max-h-0"
            }`}
          >
            <div className="mt-2">
              <div className="text-xl font-semibold text-white text-center">Configurations</div>
              <div className="flex justify-around">
                <div className="w-1/2 flex flex-col gap-4">
                  <p className="text-lg text-white text-center mb-2">Granularity</p>
                  <div className="flex justify-center ">
                    <div className="grid grid-cols-2 gap-6">
                      {Object.values(GranularityEnum).map((granularity: GranularityEnum) => {
                        const isSelected: boolean = metadataState?.isExtendedMetadata?.granularity === granularity;
                        return (
                          <button
                            key={granularity}
                            onClick={() => setGranularity(granularity)}
                            className={`min-w-[100px] min-h-[35px] rounded-full text-sm border ${isSelected ? 'bg-brandPurple-500 border-transparent text-white' : 'border-n-400 text-n-400 hover:border-brandPurple-500 hover:text-n-200'}  hover:scale-105 transition-transform `}
                          >
                            {titleCase(granularity)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    className={`flex flex-col items-center justify-center ${metadataState.isExtendedMetadata?.granularity !== GranularityEnum.GLIX && "opacity-50"}`}
                  >
                    <div className="flex justify-start mb-5 text-sm">Shot Detection Sensitivity</div>
                    <Slider
                      min={0}
                      max={1}
                      step={0.01}
                      aria-label="shot-change-detection-sensitivity"
                      focusThumbOnChange={true}
                      value={metadataState.isExtendedMetadata?.shotChangeDetectionSensitivity ?? GLIX_SHOT_DETECTION_SENSITIVITY_DEFAULT}
                      onChange={(value: number) => setShotChangeDetectionSensitivity(value)}
                      isDisabled={metadataState.isExtendedMetadata?.granularity !== GranularityEnum.GLIX}
                      width="70%"
                    >
                      <SliderTrack bg="white">
                        <SliderFilledTrack bg="brandPurple.500"/>
                      </SliderTrack>
                      <SliderThumb
                        fontSize="sm"
                        boxSize="40px"
                        bg="white"
                        color="black"
                        border="2px solid brandPurple.500"
                      >
                        {(metadataState.isExtendedMetadata?.shotChangeDetectionSensitivity ?? GLIX_SHOT_DETECTION_SENSITIVITY_DEFAULT)}
                      </SliderThumb>
                    </Slider>
                  </div>

                </div>


                <div className="flex flex-col">
                  <div className="w-[0.5px] bg-n-400 h-full mt-2"></div>
                </div>


                <div className="w-1/2 flex flex-col items-center">
                  <div className="flex flex-col items-center mb-2">
                    <p className="text-lg text-white">Visual Tags</p>
                    <span className="text-xs text-center">up to 4</span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-5 gap-x-2 pb-2">
                    {Object.values(VisualTagsEnum).map((vt: VisualTagsEnum) => {
                      const isSelected: boolean = Boolean(metadataState?.isExtendedMetadata?.visualTags.includes(vt))
                      return (
                        <button
                          key={vt}
                          onClick={() => handleVisualTags(vt)}
                          className={`min-h-[35px] px-2 rounded-full text-sm border ${isSelected ? 'bg-brandPurple-500 border-transparent text-n-200' : 'border-n-400 text-n-400 hover:border-brandPurple-500 hover:text-n-200'}  hover:scale-105 transition-transform `}
                        >
                          {snakeCaseToSentenceCase(vt)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetadataStep;